document.addEventListener('DOMContentLoaded', () => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SLIDE_DURATION = 1100;
  const BLINK_DURATION = 1100;

  /* Deterministic pseudo-random so the waveforms look organic but render
     identically on every load. */
  function mulberry32(seed) {
    return function () {
      seed |= 0;
      seed = (seed + 0x6d2b79f5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function buildBackgroundLines(container, count) {
    const rand = mulberry32(1337);
    const center = (count - 1) / 2;
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const bar = document.createElement('i');
      const cluster = 0.45 + 0.55 * Math.abs(Math.sin(i * 0.09));
      const height = Math.round((14 + 76 * rand() * cluster) * 10) / 10;
      bar.style.setProperty('--sp-lh', height);
      bar.style.setProperty('--sp-ld', `${Math.round(Math.abs(i - center) * 4)}ms`);
      fragment.appendChild(bar);
    }
    container.appendChild(fragment);
  }

  /* side: 'left' strip sits left of the heart (its LAST bar is closest),
     'right' strip sits right of the heart (its FIRST bar is closest).
     Every bar knows its distance from the heart — the beat ripple and the
     red fill both travel outward from the heart. */
  function buildStrip(container, count, seed, side) {
    const rand = mulberry32(seed);
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const bar = document.createElement('i');
      const spike = rand() < 0.12 ? 1 : 0.55;
      const height = Math.round(12 + 82 * rand() * spike);
      bar.style.setProperty('--sp-bh', Math.max(height, 8));

      bar.dataset.spDistance = side === 'left' ? count - 1 - i : i;
      fragment.appendChild(bar);
    }
    container.appendChild(fragment);
  }

  /* How far the strip reaches on screen right now. The narrow breakpoints drop
     the outer bars, so the sweep has to be measured against what is actually
     visible — running it to the full configured count would fill every visible
     bar in half the cycle and then sit still for the rest. */
  function visibleSpan(bars, fallback) {
    let max = -1;
    bars.forEach((bar) => {
      if (bar.offsetParent === null) return;
      const distance = parseFloat(bar.dataset.spDistance);
      if (distance > max) max = distance;
    });
    return max < 0 ? fallback : max + 1;
  }

  /* The ripple takes the same span of time to cross a strip on every screen.
     A fixed per-bar delay would race the wave across the shortened mobile
     strips — 270ms instead of the 640ms desktop reads as, so the bars look
     like they thump together rather than rippling out from the heart. */
  function applyRippleDelays(root) {
    const bars = Array.from(root.querySelectorAll('[data-sp-strip] i'));
    if (!bars.length) return;

    const count = parseInt(root.dataset.stripBars, 10) || 65;
    /* The span desktop produces at the configured bar count, so a full-width
       strip keeps exactly the timing it has today. */
    const rippleMs = Math.max(count - 1, 1) * 10;
    const furthest = visibleSpan(bars, count) - 1;

    bars.forEach((bar) => {
      const distance = parseFloat(bar.dataset.spDistance);
      const delay = furthest > 0 ? (distance / furthest) * rippleMs : 0;
      bar.style.setProperty('--sp-bd', `${Math.round(delay)}ms`);
    });
  }

  /* Red fill flows continuously outward from the heart on both sides at
     once, filling to ~80% (outer ends stay unfilled like the design) over
     FILL_BEATS beats, holds a moment, then restarts. */
  function startFillLoop(root, beatMs) {
    const bars = Array.from(root.querySelectorAll('[data-sp-strip] i'));
    if (!bars.length) return;

    const count = parseInt(root.dataset.stripBars, 10) || 65;
    let maxFill = visibleSpan(bars, count) * 0.8;

    /* Crossing a breakpoint changes how many bars are on screen, so the span
       is measured again once a resize settles. */
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        maxFill = visibleSpan(bars, count) * 0.8;
        applyRippleDelays(root);
      }, 150);
    });

    const FILL_BEATS = 4;
    const holdMs = beatMs;
    const fillMs = FILL_BEATS * beatMs;

    let start = null;

    function frame(now) {
      if (start === null) start = now;
      const elapsed = (now - start) % (fillMs + holdMs);
      const progress = Math.min(elapsed / fillMs, 1);
      const threshold = progress * maxFill;
      bars.forEach((bar) => {
        bar.classList.toggle('is-on', parseFloat(bar.dataset.spDistance) < threshold);
      });
      requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
  }

  function showFinalState(root) {
    root.classList.add('is-static', 'is-inview');
    const count = parseInt(root.dataset.stripBars, 10) || 65;
    const bars = Array.from(root.querySelectorAll('[data-sp-strip] i'));
    const reach = visibleSpan(bars, count) * 0.55;
    bars.forEach((bar) => {
      if (parseFloat(bar.dataset.spDistance) < reach) bar.classList.add('is-on');
    });
  }

  function initSection(root) {
    if (root.dataset.spInit) return;
    root.dataset.spInit = 'true';

    const bgBarCount = parseInt(root.dataset.bgBars, 10) || 220;
    const stripBarCount = parseInt(root.dataset.stripBars, 10) || 65;

    const lines = root.querySelector('[data-sp-lines]');
    if (lines) buildBackgroundLines(lines, bgBarCount);
    root.querySelectorAll('[data-sp-strip]').forEach((strip, index) => {
      buildStrip(strip, stripBarCount, 42 + index * 7, index === 0 ? 'left' : 'right');
    });
    applyRippleDelays(root);

    if (REDUCED_MOTION || (window.Shopify && window.Shopify.designMode)) {
      showFinalState(root);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);

          /* 1. headphones slide down (CSS transition) */
          entry.target.classList.add('is-inview');

          /* 2. background lines blink once */
          setTimeout(() => entry.target.classList.add('is-blink'), SLIDE_DURATION);

          /* 3. heart beats + bars pulse + red fill pumped per beat, continuous */
          setTimeout(() => {
            entry.target.classList.add('is-live');
            const beatValue = getComputedStyle(entry.target).getPropertyValue('--sp-beat').trim();
            const beatMs = (parseFloat(beatValue) || 1.4) * 1000;
            startFillLoop(entry.target, beatMs);
          }, SLIDE_DURATION + BLINK_DURATION);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -60px 0px' }
    );

    observer.observe(root);
  }

  /* Add to cart by handing the click to the PDP's own <product-form>, rather
     than posting to /cart/add from here. Clicking its real submit button is what
     keeps the theme in charge of the whole thing: the fetch, the button's
     loading state, error messages, the cart drawer or notification, and the
     section re-render all run exactly as they do from the main form. Anything
     written here would be a second, divergent add-to-cart path. */
  function initAddToCart(scope) {
    (scope || document).querySelectorAll('[data-sp-atc]').forEach((button) => {
      if (button.dataset.spAtcBound) return;
      button.dataset.spAtcBound = 'true';

      /* Mirrors the theme's own button rather than guessing when the request is
         done. product-form adds `loading` before the fetch and removes it in a
         finally block, so watching that one class covers success, validation
         errors and network failures alike — no separate list of events to keep in
         step with the theme. */
      const followThemeButton = (submit) => {
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');

        let done = false;

        const clear = () => {
          if (done) return;
          done = true;
          observer.disconnect();
          window.clearTimeout(failsafe);
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        };

        const observer = new MutationObserver(() => {
          if (!submit.classList.contains('loading')) clear();
        });

        /* If the theme never marks its button — a customised product form, say —
           the spinner must not be left turning forever. */
        const failsafe = window.setTimeout(clear, 15000);

        observer.observe(submit, { attributes: true, attributeFilter: ['class'] });
      };

      button.addEventListener('click', () => {
        if (button.classList.contains('is-loading')) return;

        const form = document.querySelector('product-form form[action*="/cart/add"]');

        if (!form) {
          /* Not a product page — fall back to the link if one was set. */
          const href = button.dataset.spFallback;
          if (href) window.location.href = href;
          return;
        }

        const submit = form.querySelector('[type="submit"]');

        /* Sold out or already in flight: the theme marks its own button, and
           re-entering here would queue a second add behind it. */
        if (submit && (submit.disabled || submit.getAttribute('aria-disabled') === 'true')) return;

        if (submit) {
          followThemeButton(submit);
          /* .click() over form.submit(), which bypasses the submit event the
             theme's handler is bound to and would do a full page post instead. */
          submit.click();
          return;
        }

        if (form.requestSubmit) form.requestSubmit();
      });
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-sych-pulse]').forEach(initSection);
    initAddToCart(scope);
  }

  init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
});